import { KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useToast } from "@/components/shared";
import { getApiErrorMessage } from "@/lib/api";
import { authorizationService } from "@/lib/services";
import type { AdminAuthorization } from "@/types";

const PURPOSE_LABELS: Record<string, string> = {
  data_sync: "Automated syncs",
  scheduled_flows: "Scheduled workflows",
};

function purposeLabel(purpose: string): string {
  return PURPOSE_LABELS[purpose] ?? purpose;
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/** Explains that this is an org-wide, cross-extension view of delegated access. */
function AuthorizationsIntro() {
  return (
    <div className="mb-6">
      <h2 className="text-lg font-semibold text-content-primary flex items-center gap-2">
        <ShieldCheck className="w-5 h-5" />
        Authorizations
      </h2>
      <p className="text-sm text-content-tertiary mt-1 max-w-3xl">
        Delegated permissions users have granted Anthm to act on their behalf for background work — spanning{" "}
        <em>every</em> integration (e.g. SharePoint via Microsoft), not a single extension. It's shown here rather than
        on an extension page because a single authorization can power multiple purposes (automated syncs, scheduled
        workflows) across sources. Anthm can only access what each user can already access.
      </p>
    </div>
  );
}

export function AdminAuthorizations() {
  const [rows, setRows] = useState<AdminAuthorization[]>([]);
  const [loading, setLoading] = useState(true);
  const { showToast } = useToast();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authorizationService.listAll();
      setRows(res.data);
    } catch (err) {
      showToast(getApiErrorMessage(err) || "Failed to load authorizations", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div>
        <AuthorizationsIntro />
        <div className="flex flex-col items-center justify-center text-center py-12">
          <KeyRound className="w-12 h-12 text-content-quaternary" />
          <p className="text-content-tertiary mt-3">No active authorizations across the organization.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <AuthorizationsIntro />
      <div className="bg-surface-primary border border-border-primary rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border-primary">
              <th className="text-left px-4 py-3 text-xs font-medium text-content-tertiary uppercase tracking-wider">
                User
              </th>
              <th className="text-left px-4 py-3 text-xs font-medium text-content-tertiary uppercase tracking-wider">
                Purpose
              </th>
              <th className="text-left px-4 py-3 text-xs font-medium text-content-tertiary uppercase tracking-wider">
                Data sources
              </th>
              <th className="text-left px-4 py-3 text-xs font-medium text-content-tertiary uppercase tracking-wider">
                Last used
              </th>
              <th className="text-left px-4 py-3 text-xs font-medium text-content-tertiary uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const reauth = row.syncTargets.some((t) => t.reauthorizationRequired) || !row.isValid;
              return (
                <tr
                  key={row.uuid}
                  className="border-b border-border-secondary last:border-b-0 hover:bg-interactive-hover"
                >
                  <td className="px-4 py-3 text-sm">
                    <div className="text-content-primary">{row.userName ?? "—"}</div>
                    <div className="text-content-quaternary text-xs">{row.userEmail ?? `user #${row.userId}`}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-content-secondary">{purposeLabel(row.purpose)}</td>
                  <td className="px-4 py-3 text-sm text-content-secondary">{row.syncTargets.length}</td>
                  <td className="px-4 py-3 text-sm text-content-tertiary">{formatDate(row.lastUsedAt)}</td>
                  <td className="px-4 py-3 text-sm">
                    {reauth ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400 border border-amber-500/30">
                        Re-authorization required
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                        Active
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
